import React, { useEffect, useRef, useState } from "react";
import MuxUploader from "@mux/mux-uploader-react";
import { AlertTriangle, CheckCircle2, Film, LoaderCircle, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

export default function GalleryClipUploadModal({ folders, defaultFolderId, onClose, onComplete }) {
  const [title, setTitle] = useState("");
  const [folderId, setFolderId] = useState(defaultFolderId || "");
  const [accessLevel, setAccessLevel] = useState("free");
  const [posterTime, setPosterTime] = useState(1);
  const [upload, setUpload] = useState(null);
  const [clip, setClip] = useState(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const startUpload = async (event) => {
    event.preventDefault();
    if (!title.trim()) return toast.error("Clip title required");
    setError("");
    setStatus(null);
    try {
      const response = await api.post("/api/pictures/clips/upload", {
        title: title.trim(),
        folder_id: folderId || null,
        visibility: [accessLevel],
        poster_time: Number(posterTime) || 0,
      });
      setClip({ id: response.data.picture_id, status: response.data.status });
      setUpload({ endpoint: response.data.upload_url });
      setProgress(0);
    } catch (requestError) {
      const message = requestError?.response?.data?.detail || "Could not start clip upload";
      setError(message);
      toast.error(message);
    }
  };

  useEffect(() => {
    if (!uploading || !clip?.id) return undefined;
    let stopped = false;
    let timer;
    const poll = async () => {
      try {
        const response = await api.get(`/api/pictures/clips/${clip.id}/status`);
        if (stopped) return;
        const next = response.data;
        setStatus(next);
        setClip(current => ({ ...current, status: next.status }));
        if (next.error) {
          setUploading(false);
          setError(next.error);
          return;
        }
        if (next.status === "ready" && next.poster_ready) {
          setUploading(false);
          setUpload(null);
          onCompleteRef.current?.();
          return;
        }
        if (next.status === "errored") {
          setUploading(false);
          setError(next.error || "Mux could not process this clip");
          return;
        }
      } catch (requestError) {
        if (!stopped) setError(requestError?.response?.data?.detail || "Clip status check failed; retry status to continue");
      }
      if (!stopped) timer = setTimeout(poll, 2500);
    };
    timer = setTimeout(poll, 1200);
    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, [uploading, clip?.id]);

  const handleSuccess = () => {
    setUpload(null);
    setUploading(true);
    setStatus({ status: "processing", poster_ready: false });
    setError("");
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/85 p-0 sm:p-4 backdrop-blur-sm"
      onClick={onClose} data-testid="gallery-clip-upload-modal">
      <div className="w-full max-w-xl overflow-hidden rounded-t-xl border border-white/10 bg-zinc-950 shadow-2xl sm:rounded-xl"
        onClick={event => event.stopPropagation()}>
        <header className="flex items-center justify-between border-b border-white/10 p-4">
          <div className="flex items-center gap-3">
            <Film className="text-cyan-300" size={20} />
            <div><p className="text-[10px] uppercase tracking-wider text-cyan-300">Pictures Gallery</p><h2 className="font-semibold text-white">Upload short clip</h2></div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close clip uploader" className="p-2 text-zinc-400 hover:text-white"><X size={18} /></button>
        </header>

        {!upload && !uploading && !status?.poster_ready && (
          <form onSubmit={startUpload} className="space-y-4 p-4">
            <label className="block text-xs text-zinc-300">Title
              <input required value={title} onChange={event => setTitle(event.target.value)} className="gio-input mt-1 w-full" data-testid="gallery-clip-title" />
            </label>
            <label className="block text-xs text-zinc-300">Folder
              <select value={folderId} onChange={event => setFolderId(event.target.value)} className="gio-input mt-1 w-full" data-testid="gallery-clip-folder">
                <option value="">Root / Unfiled</option>
                {folders.map(folder => <option key={folder.id} value={folder.id}>{folder.title}</option>)}
              </select>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-xs text-zinc-300">Member access
                <select value={accessLevel} onChange={event => setAccessLevel(event.target.value)} className="gio-input mt-1 w-full" data-testid="gallery-clip-access">
                  <option value="free">Free</option><option value="basic">Basic</option><option value="vip">VIP</option>
                </select>
              </label>
              <label className="block text-xs text-zinc-300">Poster frame (seconds)
                <input type="number" min="0" max="300" step="0.5" value={posterTime} onChange={event => setPosterTime(event.target.value)} className="gio-input mt-1 w-full" data-testid="gallery-clip-poster-time" />
              </label>
            </div>
            <p className="text-xs text-zinc-500">A protected poster is generated from this frame after Mux processing. The clip stays unpublished if poster generation fails.</p>
            {error && <div role="alert" className="rounded-md border border-red-500/30 bg-red-950/30 p-3 text-sm text-red-200">{error}</div>}
            <footer className="flex justify-end gap-2 border-t border-white/10 pt-4">
              <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="gallery-clip-start">Create upload</button>
            </footer>
          </form>
        )}

        {upload && (
          <div className="space-y-4 p-4">
            <div className="flex justify-between text-sm text-zinc-200"><span>{title}</span><span>{progress}%</span></div>
            <progress className="w-full accent-cyan-400" value={progress} max="100" data-testid="gallery-clip-progress" />
            <MuxUploader endpoint={upload.endpoint} accept="video/mp4,video/webm" max-file-size={500000000}
              onUploadStart={() => setUploading(true)}
              onProgress={event => {
                const value = event?.detail?.progress ?? event?.detail ?? 0;
                setProgress(Math.max(0, Math.min(100, Math.round(Number(value) * (Number(value) <= 1 ? 100 : 1)))));
              }}
              onSuccess={handleSuccess}
              onError={event => {
                const message = event?.detail?.message || event?.message || "Clip upload failed";
                setError(message);
                setUpload(null);
                setUploading(false);
                toast.error(message);
              }} />
            {error && <div role="alert" className="rounded-md border border-red-500/30 bg-red-950/30 p-3 text-sm text-red-200">{error}</div>}
          </div>
        )}

        {uploading && (
          <div className="space-y-3 p-5" role="status" data-testid="gallery-clip-processing">
            <div className="flex items-center gap-2 text-sm text-cyan-100"><LoaderCircle className="animate-spin" size={18} /> Processing clip and generating poster…</div>
            {status?.status && <p className="text-xs text-zinc-400">Mux status: {status.status}</p>}
            {error && <div role="alert" className="flex items-center gap-2 text-sm text-amber-200"><AlertTriangle size={16} />{error}</div>}
          </div>
        )}

        {status?.poster_ready && !uploading && (
          <div className="space-y-3 p-5" role="status" data-testid="gallery-clip-ready">
            <div className="flex items-center gap-2 text-sm text-emerald-200"><CheckCircle2 size={18} /> Clip ready with protected poster.</div>
            {status.duration != null && <p className="text-xs text-zinc-400">Duration: {Math.round(status.duration)} seconds</p>}
            <button type="button" onClick={onClose} className="btn-primary">Done</button>
          </div>
        )}

        {clip && !upload && !uploading && !status?.poster_ready && (
          <div className="space-y-3 p-5">
            {error && <div role="alert" className="text-sm text-amber-200">{error}</div>}
            <button type="button" onClick={() => { setError(""); setUploading(true); }} className="btn-ghost">Check upload status</button>
            <p className="text-xs text-zinc-500">This clip remains unpublished until Mux playback and its poster are ready.</p>
          </div>
        )}
      </div>
    </div>
  );
}
